import React from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import { SubmitAction } from '@lib/enums/form';
import AssetForm from 'components/Asset/AssetForm/AssetForm';
import { UseModalManagementResult } from 'routes/Assets/Asset/utils/useModalManagement';
import { UseSearchListQueriesResult } from 'routes/Assets/Asset/utils/useSearchListQueries';
import { UseAssetActionsResult } from 'routes/Assets/Asset/utils/useAssetActions';
import getDefaultValuesFromQueryData from 'routes/Assets/Asset/utils/getDefaultValuesFromQueryData';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';

interface Props {
  formRef: FormRefType;
  openDocumentsModal: () => void;
  submitNewAssetRef: React.RefObject<SubmitAction | undefined>;
  useAssetActionsResult: UseAssetActionsResult;
  useModalManagementResult: UseModalManagementResult;
  useSearchListQueriesResult: UseSearchListQueriesResult;
  isCategoriesViewOnly: boolean;
  isDocumentationLocked: boolean;
  isDocumentationPlanLocked?: boolean;
}

export default function AssetAdd(props: Props) {
  const { t } = useTranslation();

  const {
    formRef,
    openDocumentsModal,
    submitNewAssetRef,
    useModalManagementResult,
    useAssetActionsResult,
    useSearchListQueriesResult,
    isCategoriesViewOnly,
    isDocumentationLocked,
    isDocumentationPlanLocked,
  } = props;
  const {
    assetCategoryCreateMutationError,
    assetCategoryCreateMutationLoading,
    assetMutationLoading,
    assetMutationError,
    onAssetCategoryCreate,
    onCreate,
  } = useAssetActionsResult;
  const {
    closeCategoryFormModal,
    isCategoryFormModalOpened,
    openCategoryFormModal,
  } = useModalManagementResult;
  const {
    getDocumentationsLoadOptions,
    getSiteAreasLoadOptions,
    getSitesLoadOptions,
    getAssetCategoriesLoadOptions,
  } = useSearchListQueriesResult;

  return (
    <DashboardContentMaxWidth>
      <PageHeader title={t('add-new-asset')} />
      <PageBody>
        <AssetForm
          getAssetCategoriesLoadOptions={getAssetCategoriesLoadOptions}
          getDocumentationsLoadOptions={getDocumentationsLoadOptions}
          categoryGraphQLErrors={getGraphQLErrors(
            assetCategoryCreateMutationError,
          )}
          categoryLoading={assetCategoryCreateMutationLoading}
          closeCategoryFormModal={closeCategoryFormModal}
          defaultValues={getDefaultValuesFromQueryData()}
          formRef={formRef}
          graphQLErrors={getGraphQLErrors(assetMutationError)}
          isCategoryFormModalOpened={isCategoryFormModalOpened}
          loading={assetMutationLoading}
          onAssetCategoryCreate={onAssetCategoryCreate}
          onSubmit={onCreate}
          openCategoryFormModal={openCategoryFormModal}
          getSiteAreasLoadOptions={getSiteAreasLoadOptions}
          getSitesLoadOptions={getSitesLoadOptions}
          submitNewAssetRef={submitNewAssetRef}
          isCategoriesViewOnly={isCategoriesViewOnly}
          isDocumentationLocked={isDocumentationLocked}
          isDocumentationPlanLocked={isDocumentationPlanLocked}
          openDocumentsModal={openDocumentsModal}
        />
      </PageBody>
    </DashboardContentMaxWidth>
  );
}
